import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusChip } from '@/components/core';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useProfilePhotoUrl } from '../../hooks/use-profile-photo';
import { employeeName } from '../../schemas/display-name';
import { Employee } from '../../types/employee.types';

const DummyProfile = '/assets/images/dummy_profile.png';

/**
 * The head of Employee details and My Profile (prototype `emp-head`): the photo, the name, "designation ·
 * department · ID" and whether the account is active; the page's actions on the right (below on phones).
 */
export const ProfileHeader = ({
  employee,
  actions,
}: {
  employee: Employee;
  actions?: ReactNode;
}) => {
  const { t } = useTranslation();
  const { data: photoUrl, isLoading: isPhotoLoading } = useProfilePhotoUrl(employee.ProfileImageId);
  const details = [employee.Designation, employee.DepartmentName].filter(Boolean);

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-4">
        {/* Not the dummy avatar while the real photo's link is on its way. */}
        {isPhotoLoading ? (
          <Skeleton className="size-16 shrink-0 rounded-full" />
        ) : (
          <img
            src={photoUrl || DummyProfile}
            alt={employeeName(employee)}
            className="size-16 shrink-0 rounded-full border object-cover"
          />
        )}
        <div className="grid min-w-0 gap-1">
          <h1 className="break-words text-2xl font-semibold tracking-tight">
            {employeeName(employee)}
          </h1>
          <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
            {details.map((detail) => (
              <span key={detail}>{detail} · </span>
            ))}
            <span className="font-mono">{employee.EmployeeID || '-'}</span>
          </p>
          <StatusChip
            tone={employee.IsActive === false ? 'muted' : 'success'}
            className="mt-0.5 w-fit"
          >
            {employee.IsActive === false ? t('INACTIVE') : t('ACTIVE')}
          </StatusChip>
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
};
